import * as React from 'react'

import { Button, Spinner, Text } from '@factorialco/deprecated-design-system'
import { SecuritySettingsSamlSetupModalDomainFragment } from 'generated/resources/graphql'

import Tooltip from 'modules/core/components/Tooltip'
import { useAsyncData } from 'modules/core/hooks/useAsyncData'
import asyncAction from 'modules/core/lib/asyncAction'
import i18n from 'modules/core/lib/i18n'
import { apiClient as ApiClient } from 'modules/core/lib/mobx-rest'
import app from 'modules/core/store/models/app'

import SettingsGroup from 'modules/security_settings/components/SettingsGroup'

import SAMLEnabledModal from './SamlEnabled'
import SAMLSetupModal from './SamlSetupModal'
import SamlStatusTag from './SamlStatusTag'

interface Props {
  domains: SecuritySettingsSamlSetupModalDomainFragment[]
}

function SamlBlock({ domains }: Props) {
  const [openModal, setOpenModal] = React.useState(false)
  const [samlEnabled, setSamlEnabled] = React.useState(false)
  const [samlName, setSamlName] = React.useState('')

  const { loading: loading } = useAsyncData(async () => {
    const { success, value } = await asyncAction(() => ApiClient().get('/cognito/saml'), {
      successFlash: false,
    })
    if (success) {
      setSamlEnabled(value.activated)
      setSamlName(value.saml_name)
    }
  }, [])

  if (loading) {
    return <Spinner />
  }

  const afterSubmit = (success: boolean) => {
    if (success) {
      app.setFlash({
        type: 'correct',
        message: samlEnabled
          ? i18n.t('settings.edit_company.saml_sso.configuration_removed')
          : i18n.t('settings.edit_company.saml_sso.configuration_saved'),
      })
      setSamlEnabled(!samlEnabled)
    } else {
      app.setFlash({
        type: 'wrong',
        message: i18n.t('errors.generic_saving'),
      })
    }

    setOpenModal(false)
  }

  const missingDomains = !samlEnabled && !domains.some((domain) => domain.status === 'verified')

  let button = (
    <Button
      type={samlEnabled ? 'negative' : 'primary'}
      onClick={() => setOpenModal(true)}
      disabled={missingDomains}
    >
      {samlEnabled
        ? i18n.t('settings.edit_company.saml_sso.deactivate_button')
        : i18n.t('settings.edit_company.saml_sso.activate_button')}
    </Button>
  )

  if (missingDomains) {
    button = (
      <Tooltip.Alt
        attachment="top center"
        label={i18n.t('settings.edit_company.saml_sso.no_domains_tooltip')}
      >
        {button}
      </Tooltip.Alt>
    )
  }

  return (
    <SettingsGroup alignItems="flexStart">
      <SamlStatusTag enabled={samlEnabled} />
      <Text.Normal>{i18n.t('settings.edit_company.saml_sso.deactivated_description')}</Text.Normal>
      {button}
      {openModal && samlEnabled && (
        <SAMLEnabledModal onClose={() => setOpenModal(false)} onSuccess={afterSubmit} />
      )}
      {openModal && !samlEnabled && (
        <SAMLSetupModal
          onClose={() => setOpenModal(false)}
          afterSubmit={afterSubmit}
          samlName={samlName}
          domains={domains}
        />
      )}
    </SettingsGroup>
  )
}

export default SamlBlock
